import { Empty, Typography, Row, Col, Card, Table, List, Avatar, theme, Spin, Space, Tag, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
    FaUserFriends, FaFileMedical, FaUserPlus, 
    FaFileInvoiceDollar, FaHospital, FaArrowUp, FaArrowDown,
    FaUsers, FaMoneyBillWave, FaClipboardCheck,
    FaFileAlt, FaBuilding
} from 'react-icons/fa';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { staggeredContainer, staggeredItem } from '@/common/components/MotionVariants';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { get } from '../../../common/services/httpClient';
import type { SystemCounts, ApiTopCountsResponse, RecentClaims, ApiRecentClaimsResponse, RecentActivity, ApiRecentActivityResponse, TopCompaniesByClaims, ApiTopCompaniesByClaimsResponse, TopProviersByClaims, ApiTopProvidersByClaimsResponse, ClaimByStatus, ApiClaimByStatusResponse } from '../types/dashBoardTypes';
import { useAppQuery } from '../../../common/hooks/useAppQuery';
import type { GlobalToken } from "antd";
import StatusTag from '../../../common/components/Status';
import { ClaimStatus, ClaimStatusName } from '../../../common/staticData/enums';
import PieChartWithCustomizedLabel from './components/PieChartWithCustomizedLabel';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
import { useAuthStore } from '../../auth/store/authStore';
import { useState } from 'react';


const { Title, Text } = Typography;
const { useToken } = theme;

const MotionCard = motion(Card);
const MotionDiv = motion.div;

//  function for get activity icons
const getActivityIcon = (module: string, action: string, token: GlobalToken) => {
    switch (module) {
        case 'PolicyHolder':
            if (action === 'Created') return <FaUserPlus color={token.colorPrimary} />;
            if (action === 'Active') return <FaUserFriends color={token.colorSuccess} />;
            if (action === 'Cancelled') return <FaUserFriends color={token.colorError} />;
            return <FaUserFriends color={token.colorWarning} />;

        case 'Claim':
            if (action === 'Created') return <FaFileMedical color={token.colorWarning} />;
            if (action === 'Approved') return <FaFileInvoiceDollar color={token.colorSuccess} />;
            if (action === 'Rejected') return <FaFileInvoiceDollar color={token.colorError} />;
            if (action === 'AwaitingApproval') return <FaFileInvoiceDollar color={token.colorWarning} />;
            return <FaFileMedical color={token.colorInfo} />;

        case 'Policy':
            return <FaFileInvoiceDollar color={token.colorPrimary} />;

        default:
            return <FaUserFriends color={token.colorPrimary} />;
    }
};



const fetchSystemCounts = async (period: number = 15): Promise<SystemCounts> => {
    const response = await get<ApiTopCountsResponse>(
        `${API_ENDPOINTS.DASHBOARD.GET_SYSTEM_COUNTS}?period=${period}`
    );
    return response.data;
};



const fetchRecentClaims = async (): Promise<RecentClaims[]> => {
    const response = await get<ApiRecentClaimsResponse>(`${API_ENDPOINTS.DASHBOARD.GET_RECENT_CLAIMS}`);
    return response.data;
};

const fetchRecentActivities = async (): Promise<RecentActivity[]> => {
    const response = await get<ApiRecentActivityResponse>(`${API_ENDPOINTS.DASHBOARD.GET_RECENT_ACTIVITIES}`);
    return response.data;
};

const fetchTopCompaniesByClaims = async (): Promise<TopCompaniesByClaims[]> => {
    const response = await get<ApiTopCompaniesByClaimsResponse>(`${API_ENDPOINTS.DASHBOARD.GET_TOP_COMPANIES_BY_CLAIMS}`);
    return response.data;
};

const fetchTopProvidersByClaims = async (): Promise<TopProviersByClaims[]> => {
    const response = await get<ApiTopProvidersByClaimsResponse>(`${API_ENDPOINTS.DASHBOARD.GET_TOP_PROVIDERS_BY_CLAIMS}`);
    return response.data;
};

const fetchClaimByStatus = async (): Promise<ClaimByStatus[]> => {
    const response = await get<ApiClaimByStatusResponse>(`${API_ENDPOINTS.DASHBOARD.GET_CLAIM_BY_STATUS}`);
    return response.data;
};

function DashboardHomePage() {
    const { t } = useTranslation();
    const { token } = useToken();
    const userType = useAuthStore((state) => state.userType);
    const isPolicyHolder = String(userType).toLowerCase() === 'policyholder';
    const [selectedPeriod, setSelectedPeriod] = useState(15);

    //const { data: systemCounts, isLoading: isSystemLoading } = useAppQuery(
    //    ['systemCounts'],
    //    fetchSystemCounts
    //);


    const { data: systemCounts, isLoading: isSystemLoading } = useAppQuery(
        ['systemCounts', selectedPeriod],          // query key depends on selectedPeriod
        () => fetchSystemCounts(selectedPeriod)   // fetch function uses latest selectedPeriod
    );

    const { data: TopProvidersByClaims } = useAppQuery(
        ['TopProvidersByClaims'],
        fetchTopProvidersByClaims,
        { enabled: !isPolicyHolder } // <-- only fetch if not PolicyHolder
    );

    const { data: recentClaims, isLoading: isClaimsLoading } = useAppQuery(
        ['recentClaims'],
        fetchRecentClaims
    );

    const { data: recentActivities, isLoading: isActivitiesLoading } = useAppQuery(
        ['recentActivities'],
        fetchRecentActivities,
        { enabled: !isPolicyHolder } // <-- only fetch if not PolicyHolder
    );

    const { data: claimByStatus, isLoading: isClaimStatusLoading } = useAppQuery(
        ['claimByStatus'],
        fetchClaimByStatus
    );

    const { data: TopCompaniesByClaims, isLoading: isTopCompaniesLoading } = useAppQuery(
        ['TopCompaniesByClaims'],
        fetchTopCompaniesByClaims,
        { enabled: !isPolicyHolder } // <-- only fetch if not PolicyHolder
    );

    const generateSparkline = (value: number) => {
        if (!value) return [0, 0, 0, 0, 0, 0, 0];

        const absValue = Math.abs(value);

        // Positive → line goes up
        if (value > 0) {
            return [
                absValue * 0.5,
                absValue * 0.65,
                absValue * 0.7,
                absValue * 0.8,
                absValue * 0.85,
                absValue * 0.95,
                absValue
            ].map(v => Math.round(v));
        }

        // Negative → line goes down
        return [
            absValue,
            absValue * 0.95,
            absValue * 0.9,
            absValue * 0.8,
            absValue * 0.7,
            absValue * 0.6,
            absValue * 0.5
        ].map(v => Math.round(v));
    };

    // Enhanced dashboard metrics with trends - Healthcare teal theme
    const dashboardData = [
        {
            title: t('policyholders'),
            value: systemCounts?.activePolicyHolders || 0,
            icon: <FaUsers />,
            color: token.colorSuccess,
            bgColor: 'var(--app-accent-soft-2)',
            change: systemCounts?.activePolicyHoldersTrend || 0,
            trend: 'up',
            subtitle: t('active_members'),
            sparklineData: generateSparkline(systemCounts?.activePolicyHoldersTrend || 0), // [65, 78, 82, 88, 95, 102, systemCounts?.activePolicyHoldersTrend || 110]
        },
        {
            title: t('submitted_claims'),
            value: systemCounts?.submittedClaims || 0,
            icon: <FaFileAlt />,
            color: token.colorPrimary,
            bgColor: 'var(--app-accent-soft-2)',
            change: systemCounts?.submittedTrendPercent,
            trend: (systemCounts?.submittedTrendPercent || 0) < 0 ? 'down' : 'up',
            subtitle: t('awaiting_review'),
            sparklineData: generateSparkline(systemCounts?.submittedTrendPercent || 0)
        },
        {
            title: t('processed_claims'),
            value: systemCounts?.processedClaims || 0,
            icon: <FaClipboardCheck />,
            color: token.colorInfo,
            bgColor: 'var(--app-accent-soft-2)',
            change: systemCounts?.processedTrendPercent,
            trend: (systemCounts?.processedTrendPercent || 0) < 0 ? 'down' : 'up',
            subtitle: t('completed'),
            sparklineData: generateSparkline(systemCounts?.processedTrendPercent || 0)
        },
        {
            title: t('total_payout'),
            value: systemCounts?.totalPaidOut,
            icon: <FaMoneyBillWave />,
            color: token.colorWarning,
            bgColor: 'var(--app-warm-soft-1)',
            change: systemCounts?.paidOutTrendPercent,
            trend: (systemCounts?.paidOutTrendPercent || 0) < 0 ? 'down' : 'up',
            subtitle: t('this_month'),
            sparklineData: generateSparkline(systemCounts?.paidOutTrendPercent || 0),
            prefix: ''
        }
    ];

    const recentClaimsCount = recentClaims?.length ?? 0;
    const providerCount = TopProvidersByClaims?.length ?? 0;
    const providerClaimsTotal = TopProvidersByClaims?.reduce((sum, item) => sum + (item.claimCount || 0), 0) ?? 0;
    const companyCount = TopCompaniesByClaims?.length ?? 0;
    const companyClaimsTotal = TopCompaniesByClaims?.reduce((sum, item) => sum + (item.claimCount || 0), 0) ?? 0;


    const formatCurrency = (value: number | undefined, currency?: string) => {
        if (value === null || value === undefined) return '-';
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: currency || 'USD',
                maximumFractionDigits: 0,
            }).format(value);
        } catch {
            return `${value.toLocaleString()} ${currency || ''}`.trim();
        }
    };

    const claimsColumns = [
        {
            title: t('policyholder'),
            dataIndex: 'policyHolderName',
            key: 'policyHolderName',
            render: (name: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={38}
                        style={{ background: 'var(--app-gradient)', color: '#fff', fontWeight: 700 }}
                    >
                        {name?.charAt(0) || '?'}
                    </Avatar>
                    <div>
                        <Text strong style={{ display: 'block', color: token.colorText }}>
                            {name || t('unknown')}
                        </Text>
                        <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                            {t('policyholder')}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: t('amount'),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (_: number, record: RecentClaims) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Text strong style={{ color: token.colorText }}>
                        {formatCurrency(record.totalAmount, record.currencyISOCode)}
                    </Text>
                    {record.approvedAmount ? (
                        <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                            {t('approved')}: {formatCurrency(record.approvedAmount, record.currencyISOCode)}
                        </Text>
                    ) : null}
                </div>
            ),
        },
        {
            title: t('provider'),
            dataIndex: 'medicalProviderName',
            key: 'medicalProviderName',
            render: (provider: string) => (
                <div style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 10, background: token.colorFillTertiary, border: `1px solid ${token.colorBorderSecondary}` }}>
                    <Text style={{ color: token.colorText }}>{provider || t('unknown')}</Text>
                </div>
            ),
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={ClaimStatusName[ClaimStatus[status as keyof typeof ClaimStatus]]} />
            ),
        },
    ];


    //const policyTypesData = [
    //    { type: 'Individual Plan', percentage: 60, icon: <FaUserMd />, color: token.colorInfo },
    //    { type: 'Family Plan', percentage: 30, icon: <FaUserFriends />, color: token.colorWarning },
    //    { type: 'Corporate Plan', percentage: 10, icon: <FaHospital />, color: token.colorError },
    //];

    // const topProvidersData = [
    //     { name: 'General Hospital', claims: 120, avatar: 'https://via.placeholder.com/40' },
    //     { name: 'City Clinic', claims: 95, avatar: 'https://via.placeholder.com/40' },
    //     { name: 'Wellness Center', claims: 80, avatar: 'https://via.placeholder.com/40' },
    // ];

    //const recentActivitiesData = [
    //  {
    //    icon: <FaUserPlus color={token.colorPrimary} />,
    //    text: 'New policyholder: Sarah Smith',
    //    time: '2 hours ago',
    //  },
    //  {
    //    icon: <FaFileInvoiceDollar color={token.colorSuccess} />,
    //    text: 'Claim #1234 approved for $1500',
    //    time: '1 day ago',
    //  },
    //  {
    //    icon: <FaFileMedical color={token.colorWarning} />,
    //    text: 'New claim submitted by Michael Johnson',
    //    time: '2 days ago',
    //  },
    //  ];

    return (
        <div style={{ 
            padding: '0', 
            background: token.colorBgContainer,
            minHeight: '100vh',
        }}>
            {/* Modern Header with Gradient Background */}
            <div style={{
                background: token.colorBgContainer,
                padding: '16px 24px',
                marginBottom: 0,
                borderBottom: `1px solid ${token.colorBorder}`
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column' // stack title/subtitle and dropdown
                }}>
                    {/* Title + Icon and Dropdown in one row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: 'var(--app-gradient)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 16,
                                color: '#FFFFFF'
                            }}>
                                📊
                            </div>
                            <Title level={2} style={{
                                margin: 0,
                                fontSize: 26,
                                fontWeight: 700,
                                color: token.colorText,
                                letterSpacing: '-0.3px'
                            }}>
                                {t('dashboard')}
                            </Title>
                        </div>

                        {/* Dropdown on the top-right */}
                        <Select
                            value={selectedPeriod}
                            onChange={setSelectedPeriod}
                            style={{ width: 160 }}
                            options={[
                                { label: t('Last 15 Days'), value: 15 },
                                { label: t('Last 30 Days'), value: 30 },
                                { label: t('Last 60 Days'), value: 60 },
                                { label: t('Last 90 Days'), value: 90 },
                                { label: t('Last 120 Days'), value: 120 },
                            ]}
                        />
                    </div>

                    {/* Subtitle below */}
                    <Text style={{
                        fontSize: 12,
                        color: token.colorTextSecondary,
                        display: 'block',
                        fontWeight: 400,
                        marginLeft: 40
                    }}>
                        {t('dashboard_subtitle')}
                    </Text>
                </div>
            </div>

            

            <MotionDiv variants={staggeredContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                <MotionDiv
                    variants={staggeredContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}
                >
                <Row gutter={[20, 20]} style={{ marginTop: 0 }}>
                    {dashboardData.map((item, index) => (
                        <Col xs={24} sm={12} lg={6} key={index}>
                            <MotionCard 
                                variants={staggeredItem}
                                hoverable
                                style={{
                                    border: `1px solid ${token.colorBorder}`,
                                    borderRadius: 16,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                    background: token.colorBgContainer,
                                    transition: 'all 0.4s cubic-bezier(0.23, 1, 0.320, 1)',
                                    cursor: 'pointer'
                                }}
                                bodyStyle={{ padding: 24 }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = 'var(--app-shadow-soft)';
                                    e.currentTarget.style.borderColor = 'var(--app-accent-soft-1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                                    e.currentTarget.style.borderColor = token.colorBorder;
                                }}
                            >
                                <Spin spinning={isSystemLoading}>
                                    <div>
                                        {/* Header with Icon and Trend */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                                            <div style={{ 
                                                background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}08 100%)`,
                                                width: 52,
                                                height: 52,
                                                borderRadius: 12,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 24,
                                                color: item.color,
                                                border: `1px solid ${item.color}30`
                                            }}>
                                                {item.icon}
                                            </div>
                                            <Tag 
                                                color={item.trend === 'up' ? 'success' : 'error'} 
                                                style={{ 
                                                    borderRadius: 8,
                                                    padding: '6px 12px',
                                                    border: 'none',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    background: item.trend === 'up' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                                    background: item.trend === 'up' ? token.colorSuccessBg : token.colorErrorBg,
                                                    color: item.trend === 'up' ? token.colorSuccess : token.colorError
                                                }}
                                            >
                                                {item.trend === 'up' ? <FaArrowUp style={{ marginRight: 4 }} /> : <FaArrowDown style={{ marginRight: 4 }} />} {Math.abs(item.change)}%
                                            </Tag>
                                        </div>

                                        {/* Metric Details */}
                                        <Text style={{ fontSize: 13, color: token.colorTextSecondary, display: 'block', marginBottom: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {item.subtitle}
                                        </Text>
                                        <Title level={3} style={{ margin: 0, fontSize: 32, fontWeight: 900, color: token.colorText, letterSpacing: '-0.5px' }}>
                                            {item.prefix}{item.value?.toLocaleString() || 0}
                                        </Title>
                                        <Text style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 10, display: 'block' }}>
                                            {item.title}
                                        </Text>
                                    </div>

                                    {/* Mini Sparkline */}
                                    <div style={{ height: 48, marginTop: 20, opacity: 0.8 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={item.sparklineData.map((val, idx) => ({ value: val, index: idx }))}>
                                                <defs>
                                                    <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor={item.color} stopOpacity={0.4} />
                                                        <stop offset="100%" stopColor={item.color} stopOpacity={0.05} />
                                                    </linearGradient>
                                                </defs>
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="value" 
                                                    stroke={item.color} 
                                                    strokeWidth={2.5}
                                                    fill={`url(#gradient-${index})`}
                                                    dot={false}
                                                    isAnimationActive={false}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Spin>
                            </MotionCard>
                        </Col>
                    ))}
                </Row>
                </MotionDiv>
               
                {/* Recent Claims Table & Claims by Status Pie Chart */}
                <MotionDiv
                    variants={staggeredContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}
                >
                    <Row gutter={[20, 20]} style={{ marginTop: 0 }} align="stretch">
                        <Col xs={24} lg={14}>
                            <MotionDiv variants={staggeredItem}>
                                <Card 
                                    style={{ 
                                        borderRadius: 16, 
                                        border: `1px solid ${token.colorBorder}`,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                        background: token.colorBgContainer,
                                        overflow: 'hidden',
                                        height: '100%',
                                        minHeight: 490
                                    }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                        <div style={{ 
                                            background: 'var(--app-accent-soft-2)',
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            color: token.colorSuccess,
                                            border: `1px solid ${token.colorSuccessBorder}`,
                                            flexShrink: 0
                                        }}>
                                            <FaFileInvoiceDollar />
                                        </div>
                                        <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: token.colorText }}>
                                            {t('recent_claims')}
                                        </Title>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                                        <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                            {t('live_feed')} • {recentClaimsCount} {recentClaimsCount === 1 ? t('record') : t('records')}
                                        </Text>
                                        <Space size={8} wrap>
                                            <Tag color={token.colorPrimary} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: 'var(--app-accent-soft-2)', color: token.colorPrimary }}>
                                                {t('live')}
                                            </Tag>
                                            <Tag color={token.colorSuccess} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorSuccessBg, color: token.colorSuccess }}>
                                                {recentClaimsCount} {t('new')}
                                            </Tag>
                                        </Space>
                                    </div>
                                    <Spin spinning={isClaimsLoading}>
                                        <Table 
                                            rowKey={(_, index) => `recent-claim-${index ?? 0}`}
                                            columns={claimsColumns} 
                                            dataSource={recentClaims} 
                                            pagination={false}
                                            size="middle"
                                            style={{ marginTop: 0 }}
                                            scroll={{ x: true }}
                                            onHeaderRow={() => ({
                                                style: {
                                                    background: token.colorFillSecondary,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.2,
                                                    fontWeight: 700
                                                }
                                            })}
                                            onRow={(_, index = 0) => ({
                                                style: {
                                                    background: index % 2 === 0 ? token.colorBgElevated : token.colorFillAlter,
                                                    borderBottom: `1px solid ${token.colorBorder}`
                                                }
                                            })}
                                        />
                                    </Spin>
                                </Card>
                            </MotionDiv>
                        </Col>
                        <Col xs={24} lg={10}>
                            <MotionDiv variants={staggeredItem}>
                                <Card 
                                    style={{ 
                                        borderRadius: 16, 
                                        border: `1px solid ${token.colorBorder}`,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                        background: token.colorBgContainer,
                                        overflow: 'hidden',
                                        height: '100%'
                                    }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                        <div style={{ 
                                            background: 'var(--app-accent-soft-2)',
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            color: token.colorInfo,
                                            border: `1px solid ${token.colorInfoBorder}`,
                                            flexShrink: 0
                                        }}>
                                            <FaClipboardCheck />
                                        </div>
                                        <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: token.colorText }}>
                                            {t('claim_distribution')}
                                        </Title>
                                    </div>
                                    <Spin spinning={isClaimStatusLoading}>
                                        <PieChartWithCustomizedLabel data={claimByStatus} />
                                    </Spin>
                                </Card>
                            </MotionDiv>
                        </Col>
                    </Row>
                </MotionDiv>


                {/* Recent Activities */}
                {userType && String(userType).toLowerCase() != 'policyholder' && (
                    <MotionDiv
                        variants={staggeredContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.25 }}
                        style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}
                    >
                        <Row gutter={[20, 20]} style={{ marginTop: 0 }}>
                            <Col xs={24}>
                                <MotionDiv variants={staggeredItem}>
                                    <Card
                                        style={{
                                            borderRadius: 16,
                                            border: `1px solid ${token.colorBorder}`,
                                            boxShadow: 'var(--app-shadow-soft)',
                                            background: token.colorBgContainer,
                                            overflow: 'hidden'
                                        }}
                                        bodyStyle={{ padding: 28 }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                            <div style={{
                                                background: 'var(--app-accent-soft-2)',
                                                width: 44,
                                                height: 44,
                                                borderRadius: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 20,
                                                color: token.colorPrimary,
                                                border: `1px solid ${token.colorPrimaryBorder}`,
                                                flexShrink: 0
                                            }}>
                                                <FaClipboardCheck />
                                            </div>
                                            <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: token.colorText }}>
                                                {t('system_activity')}
                                            </Title>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                            <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                                {t('recent_audit_events')}
                                            </Text>
                                            <Space size={8} wrap>
                                                <Tag color={token.colorInfo} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorInfoBg, color: token.colorInfo }}>
                                                    {recentActivities?.length ?? 0} {t('events')}
                                                </Tag>
                                            </Space>
                                        </div>
                                        <Spin spinning={isActivitiesLoading}>
                                            {recentActivities && recentActivities.length > 0 ? (
                                                <div>
                                                    {recentActivities.slice(0, 6).map((item, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '14px 16px 14px 22px',
                                                                marginBottom: 12,
                                                                background: token.colorBgElevated,
                                                                borderRadius: 12,
                                                                border: `1px solid ${token.colorBorderSecondary}`,
                                                                transition: 'all 0.25s ease',
                                                                boxShadow: 'var(--app-shadow-soft)'
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                                        >
                                                            <div style={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 2, background: token.colorBorderSecondary, opacity: 0.5 }} />
                                                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: token.colorInfo, marginRight: 12, boxShadow: '0 0 0 4px var(--app-accent-soft-2)' }} />
                                                            <div style={{
                                                                background: 'var(--app-accent-soft-2)',
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: 8,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                marginRight: 12,
                                                                fontSize: 18,
                                                                color: token.colorInfo,
                                                                flexShrink: 0,
                                                                border: `1px solid ${token.colorInfoBorder}`
                                                            }}>
                                                                {getActivityIcon(item.moduleName, item.action, token)}
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Text strong style={{ fontSize: 13, color: token.colorText, display: 'block' }}>
                                                                    {item.description}
                                                                </Text>
                                                                <Space size={10} style={{ marginTop: 4 }}>
                                                                    <Text type="secondary" style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                                                        {item.timeAgo}
                                                                    </Text>
                                                                    <Tag style={{ borderRadius: 999, border: 'none', background: token.colorInfoBg, color: token.colorInfo, fontSize: 11, fontWeight: 700 }}>
                                                                        {item.moduleName}
                                                                    </Tag>
                                                                </Space>
                                                            </div>
                                                            <Tag
                                                                style={{
                                                                    borderRadius: 6,
                                                                    border: 'none',
                                                                    background: item.action === 'Created' ? token.colorPrimaryBg :
                                                                        item.action === 'Approved' ? token.colorSuccessBg :
                                                                            item.action === 'Rejected' ? token.colorErrorBg : token.colorInfoBg,
                                                                    color: item.action === 'Created' ? token.colorPrimary :
                                                                        item.action === 'Approved' ? token.colorSuccess :
                                                                            item.action === 'Rejected' ? token.colorError : token.colorInfo,
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    marginLeft: 8,
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {item.action}
                                                            </Tag>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <Empty description={t("no_data")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                            )}
                                        </Spin>
                                    </Card>
                                </MotionDiv>
                            </Col>
                        </Row>
                    </MotionDiv>
                )}

                {/* Top Providers & Companies */}
                {userType && String(userType).toLowerCase() != 'policyholder' && (
                    <MotionDiv
                        variants={staggeredContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.25 }}
                        style={{ padding: '32px 24px', maxWidth: '1200px', margin: '0 auto' }}
                    >
                    <Row gutter={[20, 20]} style={{ marginTop: 0 }}>
                        <Col xs={24} lg={12}>
                            <MotionDiv variants={staggeredItem}>
                                <Card 
                                    style={{ 
                                        borderRadius: 16, 
                                        border: `1px solid ${token.colorBorder}`,
                                        boxShadow: 'var(--app-shadow-soft)',
                                        background: token.colorBgContainer,
                                        overflow: 'hidden',
                                        height: '100%'
                                    }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                        <div style={{ 
                                            background: 'var(--app-accent-soft-2)',
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            color: token.colorPrimary,
                                            border: `1px solid ${token.colorPrimaryBorder}`,
                                            flexShrink: 0
                                        }}>
                                            <FaHospital />
                                        </div>
                                        <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: token.colorText }}>
                                            {t('healthcare_network')}
                                        </Title>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                        <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                            {t('curated_providers')}
                                        </Text>
                                        <Space size={8} wrap>
                                            <Tag color={token.colorPrimary} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorPrimaryBg, color: token.colorPrimary }}>
                                                {providerCount} {t('providers')}
                                            </Tag>
                                            <Tag color={token.colorSuccess} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorSuccessBg, color: token.colorSuccess }}>
                                                {providerClaimsTotal} {t('claims')}
                                            </Tag>
                                        </Space>
                                    </div>
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={TopProvidersByClaims?.slice(0, 5)}
                                        renderItem={(item, index) => (
                                            <List.Item 
                                                style={{ 
                                                    padding: '14px 16px',
                                                    marginBottom: 12,
                                                    background: token.colorBgElevated,
                                                    borderRadius: 12,
                                                    border: `1px solid ${token.colorBorderSecondary}`,
                                                    boxShadow: 'var(--app-shadow-soft)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <div style={{ position: 'relative' }}>
                                                            <Avatar 
                                                                size={44}
                                                                src={item.logo ? `${VITE_API_BASE_URL.replace(/api$/, '')}${item.logo}` : undefined}
                                                                style={{ background: 'var(--app-gradient)' }}
                                                            >
                                                                <FaHospital />
                                                            </Avatar>
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                background: 'var(--app-gradient)',
                                                                color: '#fff',
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                boxShadow: 'var(--app-shadow-soft)'
                                                            }}>
                                                                {index + 1}
                                                            </div>
                                                        </div>
                                                    }
                                                    title={
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <Text strong style={{ fontSize: 14, color: token.colorText }}>{item.name}</Text>
                                                            <Tag style={{ borderRadius: 999, border: 'none', background: token.colorSuccessBg, color: token.colorSuccess, fontWeight: 600 }}>
                                                                #{index + 1}
                                                            </Tag>
                                                        </div>
                                                    }
                                                    description={
                                                        <Space size={10}>
                                                            <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                                                {item.claimCount} claims processed
                                                            </Text>
                                                            <div style={{ height: 4, width: 80, borderRadius: 999, background: token.colorFillSecondary, overflow: 'hidden' }}>
                                                                <div style={{ width: `${Math.min(100, item.claimCount * 2)}%`, height: '100%', background: 'var(--app-gradient)' }} />
                                                            </div>
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            </MotionDiv>
                        </Col>
                        <Col xs={24} lg={12}>
                            <MotionDiv variants={staggeredItem}>
                                <Card 
                                    style={{ 
                                        borderRadius: 16, 
                                        border: `1px solid ${token.colorBorder}`,
                                        boxShadow: 'var(--app-shadow-soft)',
                                        background: token.colorBgContainer,
                                        overflow: 'hidden',
                                        height: '100%'
                                    }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                        <div style={{ 
                                            background: 'var(--app-accent-soft-2)',
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            color: token.colorPrimary,
                                            border: `1px solid ${token.colorPrimaryBorder}`,
                                            flexShrink: 0
                                        }}>
                                            <FaBuilding />
                                        </div>
                                        <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: token.colorText }}>
                                            {t('corporate_partners')}
                                        </Title>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                        <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                            {t('top_corporate_allies')}
                                        </Text>
                                        <Space size={8} wrap>
                                            <Tag color={token.colorInfo} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorInfoBg, color: token.colorInfo }}>
                                                {companyCount} {t('partners')}
                                            </Tag>
                                            <Tag color={token.colorPrimary} style={{ borderRadius: 999, padding: '4px 10px', border: 'none', fontWeight: 600, background: token.colorPrimaryBg, color: token.colorPrimary }}>
                                                {companyClaimsTotal} {t('claims')}
                                            </Tag>
                                        </Space>
                                    </div>
                                    {isTopCompaniesLoading ? (
                                        <Spin />
                                    ) : (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={TopCompaniesByClaims?.slice(0, 5) ?? []}
                                            renderItem={(item, index) => (
                                                <List.Item 
                                                    key={item.name}
                                                    style={{ 
                                                        padding: '14px 16px',
                                                        marginBottom: 12,
                                                        background: token.colorBgElevated,
                                                        borderRadius: 12,
                                                        border: `1px solid ${token.colorBorderSecondary}`,
                                                        boxShadow: 'var(--app-shadow-soft)',
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <div style={{ position: 'relative' }}>
                                                                <Avatar 
                                                                    size={44}
                                                                    src={item.logo ? `${VITE_API_BASE_URL.replace(/api$/, '')}${item.logo}` : undefined}
                                                                    style={{ background: 'var(--app-gradient)' }}
                                                                >
                                                                    {item.name?.charAt(0)}
                                                                </Avatar>
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: -8,
                                                                    right: -8,
                                                                    background: 'var(--app-gradient)',
                                                                    color: '#fff',
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: '50%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    boxShadow: 'var(--app-shadow-soft)'
                                                                }}>
                                                                    {index + 1}
                                                                </div>
                                                            </div>
                                                        }
                                                        title={
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <Text strong style={{ fontSize: 14, color: token.colorText }}>{item.name}</Text>
                                                                <Tag style={{ borderRadius: 999, border: 'none', background: token.colorInfoBg, color: token.colorInfo, fontWeight: 600 }}>
                                                                    #{index + 1}
                                                                </Tag>
                                                            </div>
                                                        }
                                                        description={
                                                            <Space size={10} style={{ marginTop: 4 }}>
                                                                <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                                                    {item.claimCount} claims
                                                                </Text>
                                                                <div style={{ height: 4, width: 80, borderRadius: 999, background: token.colorFillSecondary, overflow: 'hidden' }}>
                                                                    <div style={{ width: `${Math.min(100, item.claimCount * 2)}%`, height: '100%', background: 'var(--app-gradient)' }} />
                                                                </div>
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    )}
                                </Card>
                            </MotionDiv>
                        </Col>
                    </Row>
                    </MotionDiv>
                )}

                

            </MotionDiv>
        </div>
    );
}

export default DashboardHomePage;




